import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from 'components/layouts/modal';
import { RadioInput, TextInput } from 'components/core';
import useApi from 'utils/use-api';
import wrapClick from 'utils/wrap-click';
import toast from 'react-hot-toast';

interface ReminderModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  requestId: number;
  onSuccess: () => void;
}

const REMINDER_TYPES = ['30m', '1h', '4h', '12h', '24h', '3d', '7d', '30d', 'custom'] as const;

const validationSchema = Yup.object({
  reminderType: Yup.string()
    .oneOf(REMINDER_TYPES as unknown as string[], 'Invalid reminder type')
    .required('Reminder type is required'),
  customDays: Yup.number().when('reminderType', {
    is: 'custom',
    then: schema => schema.required('Custom hours is required').min(1, 'Must be at least 1 hour').max(720, 'Cannot exceed 30 days'),
  }),
});

const ReminderModal: FC<ReminderModalProps> = ({ open, setOpen, requestId, onSuccess }) => {
  const api = useApi();

  const form = useFormik({
    initialValues: {
      reminderType: '24h',
      customDays: 1,
    },
    validationSchema,
    onSubmit: async values => {
      await setReminderMutation.mutateAsync(values);
    },
  });

  const setReminderMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      let minutes = 60;
      if (values.reminderType === '30m') minutes = 30;
      else if (values.reminderType === '1h') minutes = 60;
      else if (values.reminderType === '4h') minutes = 240;
      else if (values.reminderType === '12h') minutes = 720;
      else if (values.reminderType === '24h') minutes = 1440;
      else if (values.reminderType === '3d') minutes = 3 * 1440;
      else if (values.reminderType === '7d') minutes = 7 * 1440;
      else if (values.reminderType === '30d') minutes = 30 * 1440;
      else if (values.reminderType === 'custom') minutes = values.customDays * 60;

      const response = await api.post(`/access-requests/${requestId}/reminder`, { minutes });
      return response.data;
    },
    onSuccess: () => {
      form.resetForm();
      onSuccess();
      toast(JSON.stringify({ type: 'success', title: 'Reminder set' }));
    },
  });

  const reminderOptions = [
    { value: '30m', label: '30 Minutes', description: 'Remind me in 30 minutes' },
    { value: '1h', label: '1 Hour', description: 'Remind me in 1 hour' },
    { value: '4h', label: '4 Hours', description: 'Remind me in 4 hours' },
    { value: '12h', label: '12 Hours', description: 'Remind me in 12 hours' },
    { value: '24h', label: '24 Hours', description: 'Remind me in 24 hours' },
    { value: '3d', label: '3 Days', description: 'Remind me in 3 days' },
    { value: '7d', label: '7 Days', description: 'Remind me in 7 days' },
    { value: '30d', label: '30 Days', description: 'Remind me in 30 days' },
    { value: 'custom', label: 'Custom', description: 'Set a custom reminder period' },
  ];

  const renderActions = () => {
    return (
      <button
        type="button"
        onClick={wrapClick(() => form.handleSubmit())}
        disabled={setReminderMutation.isPending}
        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {setReminderMutation.isPending ? 'Setting...' : 'Set Reminder'}
      </button>
    );
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Set Reminder"
      description="Choose when you'd like to be reminded about this request"
      size="3xl"
      renderActions={renderActions}
    >
      <div className="space-y-6 mt-4">
        <div>
          <RadioInput id="reminderType" label="Select Reminder Period" options={reminderOptions} variant="grid" {...form} />
        </div>

        {form.values.reminderType === 'custom' && (
          <div>
            <TextInput
              id="customDays"
              label="Custom Hours"
              type="number"
              placeholder="Enter number of hours"
              required={true}
              min={1}
              max={720}
              {...form}
            />
            <p className="mt-2 text-sm text-gray-500">Maximum: 720 hours (30 days)</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReminderModal;
